import { useEffect, useRef, useState } from 'react';
import { getShipmentPdf } from '@/api/client';
import { Icon } from '@/components/Icon';
import { useFormFactor } from '@/lib/formFactor';
import { num, qty } from '@/lib/format';
import { fullTime } from './format';
import type { ShipLineGroup } from './bundle';
import type { ShipmentListItem, ShipmentOrder } from './types';
import s from './ShipmentPrintDialog.module.css';

type PrintLine = { key: string; name: string; code: string; quantity: string; detail?: string };

function maskPhone(value?: string | null) {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  return digits.length >= 7 ? `${digits.slice(0, 3)}****${digits.slice(-4)}` : value;
}

function printLines(item: ShipmentListItem, groups: ShipLineGroup[] | null): { lines: PrintLine[]; flat: boolean } {
  if (!groups || groups.some((group) => group.kind === 'bundle' && group.sets === null)) {
    return {
      flat: true,
      lines: item.lines.map((line) => ({
        key: line.id, name: line.product.name, code: line.product.code,
        quantity: `${qty(line.quantity)} 个`,
      })),
    };
  }
  return {
    flat: false,
    lines: groups.map((group) => group.kind === 'bundle' ? ({
      key: group.key, name: group.name, code: group.code || '—',
      quantity: `${qty(group.sets)} 套`,
      detail: group.lines.map((line) => `${line.product.name} ${qty(line.quantity)} 个`).join(' · '),
    }) : ({
      key: group.key, name: group.line.product.name, code: group.line.product.code,
      quantity: `${qty(group.line.quantity)} 个`,
    })),
  };
}

export function ShipmentPrintDialog({ item, order, groups, onClose }: {
  item: ShipmentListItem;
  order?: ShipmentOrder;
  groups: ShipLineGroup[] | null;
  onClose: () => void;
}) {
  const ff = useFormFactor();
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [previewPage, setPreviewPage] = useState(1);
  const [pdfPreviewError, setPdfPreviewError] = useState('');
  const [shareOpen, setShareOpen] = useState(ff === 'tablet');
  const [printOpen, setPrintOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfUrlRef = useRef('');
  const [pdfError, setPdfError] = useState('');
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  useEffect(() => {
    const controller = new AbortController();
    void getShipmentPdf(item.id, controller.signal)
      .then((blob) => {
        const file = new File([blob], `发货单-${item.shipmentNo}.pdf`, { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = url;
        setPdfFile(file);
        setPdfUrl(url);
      })
      .catch((cause: Error) => { if (cause.name !== 'AbortError') setPdfError(cause.message); });
    return () => controller.abort();
  }, [item.id, item.shipmentNo]);
  useEffect(() => () => { if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current); }, []);
  useEffect(() => { setPreviewPage(1); }, [pdfFile]);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!['tablet', 'phone'].includes(ff) || !pdfFile || !canvas) return;
    let cancelled = false;
    let documentTask: import('pdfjs-dist').PDFDocumentLoadingTask | undefined;
    let renderTask: { cancel: () => void; promise: Promise<void> } | undefined;
    let resizeObserver: ResizeObserver | undefined;
    setPdfPreviewError('');
    const render = async () => {
      try {
        const [{ getDocument, GlobalWorkerOptions }, worker] = await Promise.all([
          import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ]);
        if (cancelled) return;
        GlobalWorkerOptions.workerSrc = worker.default;
        documentTask = getDocument({ data: new Uint8Array(await pdfFile.arrayBuffer()) });
        const pdf = await documentTask.promise;
        if (cancelled) return;
        setPageCount(pdf.numPages);
        const surface = canvas.parentElement;
        if (!surface) return;
        let lastWidth = 0;
        const draw = async () => {
          const bounds = surface.getBoundingClientRect();
          if (cancelled || bounds.width < 200 || Math.abs(bounds.width - lastWidth) < 2 || !canvasRef.current) return;
          lastWidth = bounds.width;
          renderTask?.cancel();
          const page = await pdf.getPage(Math.min(previewPage, pdf.numPages));
          if (cancelled || !canvasRef.current) return;
          const base = page.getViewport({ scale: 1 });
          const ratio = window.devicePixelRatio || 1;
          const viewport = page.getViewport({ scale: bounds.width / base.width * ratio });
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          canvas.style.width = `${Math.ceil(viewport.width / ratio)}px`;
          canvas.style.height = `${Math.ceil(viewport.height / ratio)}px`;
          const context = canvas.getContext('2d');
          if (!context) throw new Error('无法创建 PDF 预览画布');
          const task = page.render({ canvas, canvasContext: context, viewport });
          renderTask = task;
          try { await task.promise; }
          catch (cause) { if ((cause as Error).name !== 'RenderingCancelledException') throw cause; }
        };
        resizeObserver = new ResizeObserver(() => { void draw().catch((cause) => { if (!cancelled) setPdfPreviewError(cause instanceof Error ? cause.message : 'PDF 页面预览失败'); }); });
        resizeObserver.observe(surface);
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        await draw();
      } catch (cause) {
        if (!cancelled) setPdfPreviewError(cause instanceof Error ? cause.message : 'PDF 页面预览失败');
      }
    };
    void render();
    return () => { cancelled = true; resizeObserver?.disconnect(); renderTask?.cancel(); void documentTask?.destroy(); };
  }, [ff, pdfFile, previewPage]);
  const sharePdf = async () => {
    if (!pdfFile || !navigator.share || !navigator.canShare?.({ files: [pdfFile] })) return;
    try { await navigator.share({ title: `发货单 ${item.shipmentNo}`, files: [pdfFile] }); }
    catch (cause) { if (!(cause instanceof DOMException && cause.name === 'AbortError')) setPdfError(cause instanceof Error ? cause.message : '分享失败'); }
  };
  const canSharePdf = !!pdfFile && !!navigator.share && !!navigator.canShare?.({ files: [pdfFile] });
  const { lines, flat } = printLines(item, groups);
  const loss = item.lines.reduce((sum, line) => sum + num(line.lossQuantity), 0);
  const dateLabel = new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric', weekday: 'short', timeZone: 'Asia/Shanghai' }).format(new Date());
  const fileSize = pdfFile ? `${Math.max(1, Math.round(pdfFile.size / 1024))} KB` : '正在生成 PDF…';
  const phonePreview = ff === 'phone';
  const copyFileName = async () => {
    if (!pdfFile) return;
    try { await navigator.clipboard.writeText(pdfFile.name); setCopyStatus('已拷贝文件名'); }
    catch { setCopyStatus('当前浏览器无法写入剪贴板'); }
  };
  if (ff === 'tablet') return <div className={`${s.scrim} ${s.tabletScrim}`} onClick={onClose}>
    <section className={s.tabletDialog} role="dialog" aria-modal="true" aria-label="发货单分享与打印" onClick={(event) => event.stopPropagation()}>
      <div className={s.tabletFrame}>
        <div className={s.tabletStatus}><span>9:41　{dateLabel}</span><span>●●●　Wi-Fi　100%</span></div>
        <header className={s.tabletHeader}>
          <button type="button" className={s.done} onClick={onClose}>完成</button>
          <div className={s.title}><strong>发货单</strong><small>{item.shipmentNo} · A4 纵向 · {pageCount ? `共 ${pageCount} 页` : '生成 PDF…'}</small></div>
          <div className={s.headerActions}>
            <button type="button" className={s.shareToggle} aria-label="分享发货单" aria-expanded={shareOpen} onClick={() => { setShareOpen((open) => !open); setPrintOpen(false); }}><Icon name="share" size={20} /></button>
            <button type="button" className={s.printButton} onClick={() => { setPrintOpen((open) => !open); setShareOpen(false); }} disabled={!pdfUrl}><Icon name="print" size={18} />打印</button>
          </div>
        </header>
        <div className={s.tabletBody}>
          <aside className={s.sidebar}>
            <section><h3>打印内容</h3><div className={s.contentTypes}><button disabled>拣货单</button><button className={s.active} aria-pressed="true">发货单</button><button disabled>物流面单</button></div></section>
            <section><h3>范围</h3><p>1 个批次 · {item.shipmentNo}</p></section>
            <section><h3>选项</h3>
              <div className={s.options}>
                <div className={s.option}><span><b>套装保留分组、组件缩进</b><small>客户按套订货，按件核收</small></span><span className={s.switchOn} aria-hidden="true" /></div>
                <div className={s.option}><span><b>留空「实收」列</b><small>收货方现场填写</small></span><span className={s.switchOn} aria-hidden="true" /></div>
                <div className={`${s.option} ${s.amountOff}`}><span><b>显示单价和金额</b><small>发货单不印金额</small></span><span className={s.switchOff} aria-hidden="true" /></div>
              </div>
            </section>
            <section><h3>纸张</h3><p>A4 · 纵向</p></section>
            {pdfError && <p className={s.error} role="alert">{pdfError}</p>}
            {flat && <p className={s.error}>订单明细暂不可用，套装按产品组件逐行列示。</p>}
            {copyStatus && <p className={s.copyStatus} role="status">{copyStatus}</p>}
          </aside>
          <main className={s.paperArea}>
            <div className={s.paperStack}>
              {pdfPreviewError ? <p className={s.error} role="alert">{pdfPreviewError}</p> : <div className={s.pageSurface}><canvas ref={canvasRef} aria-label={`发货单 PDF 第 ${previewPage} 页`} />{!pdfUrl && <span className={s.loading}>正在生成 PDF…</span>}</div>}
            </div>
            <div className={s.pager}><button type="button" aria-label="上一页" onClick={() => setPreviewPage((page) => Math.max(1, page - 1))} disabled={!pdfUrl || previewPage <= 1}>‹</button><span>{previewPage} / {pageCount || '…'}</span><button type="button" aria-label="下一页" onClick={() => setPreviewPage((page) => Math.min(pageCount, page + 1))} disabled={!pdfUrl || previewPage >= pageCount}>›</button></div>
          </main>
          {shareOpen && <section className={s.sharePopover} aria-label="系统分享">
            <div className={s.fileCard}><span className={s.fileIcon}><Icon name="file" size={20} /></span><div><b>{pdfFile?.name || `发货单-${item.shipmentNo}.pdf`}</b><small>PDF · {pageCount ? `${pageCount} 页` : '读取页数中…'} · {fileSize} · 不含金额</small></div></div>
            <div className={s.shareTargets}>
              {([
                ['wechat', '微信'], ['send', '飞书'], ['airdrop', '隔空投送'], ['mail', '邮件'],
              ] as const).map(([kind, label]) => <button type="button" key={kind} onClick={() => { if (canSharePdf) void sharePdf(); }} disabled={!canSharePdf} title={canSharePdf ? `通过${label}分享 PDF` : '当前浏览器不支持文件系统分享'}><span className={`${s.targetIcon} ${s[kind]}`}>{kind === 'wechat' ? '●' : kind === 'send' ? <Icon name="send" size={20} /> : kind === 'airdrop' ? '⌁' : <Icon name="file" size={18} />}</span>{label}</button>)}
            </div>
            <div className={s.shareActions}>
              <button type="button" onClick={() => { if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer'); }} disabled={!pdfUrl}><Icon name="print" size={16} /><span>打印<small>AirPrint</small></span></button>
              <a href={pdfUrl || undefined} download={pdfFile?.name || `发货单-${item.shipmentNo}.pdf`} aria-disabled={!pdfUrl}><Icon name="file" size={16} /><span>存储到“文件”</span></a>
              <button type="button" onClick={() => void copyFileName()} disabled={!pdfFile}><Icon name="copy" size={16} /><span>拷贝</span></button>
            </div>
          </section>}
          {printOpen && <section className={`${s.sharePopover} ${s.printPopover}`} aria-label="打印机选项">
            <h3>打印机选项</h3><div className={s.printRow}><span>打印机</span><b>选择系统打印机　›</b></div><div className={s.printRow}><span>纸张 / 范围</span><b>A4 · 全部 {pageCount || '…'} 页</b></div>
            <button type="button" className={s.printSubmit} onClick={() => { if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer'); }} disabled={!pdfUrl}>打开 PDF · {pageCount || '…'} 页</button>
          </section>}
        </div>
      </div>
    </section>
  </div>;
  if (phonePreview) return <div className={`${s.scrim} ${s.phoneScrim}`} onClick={onClose}>
    <section className={s.phoneDialog} role="dialog" aria-modal="true" aria-label="发货单 PDF 预览" onClick={(event) => event.stopPropagation()}>
      <header className={s.phoneHeader}>
        <button type="button" onClick={onClose}>完成</button>
        <div className={s.phoneTitle}><strong>发货单</strong><small>{item.shipmentNo} · A4 · {pageCount ? `共 ${pageCount} 页` : '正在生成 PDF…'}</small></div>
        <button type="button" aria-label="分享发货单" onClick={() => void sharePdf()} disabled={!canSharePdf} title={canSharePdf ? '系统分享 PDF' : '当前设备暂不支持分享 PDF'}><Icon name="share" size={18} /></button>
      </header>
      <div className={s.phoneBadges}><span>实收留空</span><span>套装分组缩进</span><span>不印金额</span></div>
      {pdfError && <p className={s.error} role="alert">{pdfError}</p>}
      <main className={s.phonePaperArea}>
        {pdfPreviewError ? <p className={s.error} role="alert">{pdfPreviewError}</p> : <div className={s.phonePaperSurface}>
          <canvas ref={canvasRef} aria-label={`发货单 PDF 第 ${previewPage} 页`} />
          {!pdfUrl && <span className={s.loading}>正在生成 PDF…</span>}
        </div>}
        <div className={s.phonePager}><button type="button" aria-label="上一页" onClick={() => setPreviewPage((page) => Math.max(1, page - 1))} disabled={!pdfUrl || previewPage <= 1}>‹</button><span>{previewPage} / {pageCount || '…'} · 双指放大</span><button type="button" aria-label="下一页" onClick={() => setPreviewPage((page) => Math.min(pageCount, page + 1))} disabled={!pdfUrl || previewPage >= pageCount}>›</button></div>
      </main>
      <footer className={s.phoneFooter}>
        <button type="button" onClick={() => void sharePdf()} disabled={!canSharePdf}><Icon name="share" size={16} />分享</button>
        <button type="button" className={s.phonePrint} onClick={() => { if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener,noreferrer'); }} disabled={!pdfUrl}><Icon name="print" size={16} />打印</button>
      </footer>
    </section>
  </div>;
  return <div className={s.scrim} onClick={onClose}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label="打印发货单" onClick={(event) => event.stopPropagation()}>
      <header className={s.controls}>
        <strong>打印发货单 · {item.shipmentNo}</strong>
        <button type="button" onClick={onClose}>取消</button>
        {canSharePdf ? <button type="button" onClick={() => void sharePdf()}>分享 PDF</button> : <a className={s.pdfAction} href={pdfUrl} download={`${item.shipmentNo}.pdf`}>{pdfFile ? '下载 PDF' : '生成 PDF…'}</a>}
        <button type="button" className={s.primary} onClick={() => window.print()}>打印</button>
      </header>
      {pdfError && <p className={s.pdfError} role="alert">{pdfError}</p>}
      <div className={s.preview}>
        <article className={s.sheet}>
          <h1>发货单</h1>
          <div className={s.meta}>
            <span>发货批次：{item.shipmentNo}</span><span>发货时间：{fullTime(item.shippedAt)}</span>
            <span>业务订单：{item.order.orderNo}</span><span>客户：{item.order.customer.name}</span>
            <span>出货仓库：{item.warehouse?.name || '—'}</span><span>出库人：{item.createdBy?.name || '—'}</span>
            <span>物流公司：{item.logisticsCompany || '—'}</span><span>物流单号：{item.trackingNo || '—'}</span>
          </div>
          {order && (order.recipientName || order.recipientPhone || order.recipientAddress) &&
            <p className={s.recipient}>收货信息：{[order.recipientName, maskPhone(order.recipientPhone), order.recipientAddress].filter(Boolean).join(' · ')}</p>}
          <table>
            <thead><tr><th>产品</th><th>产品编号</th><th>本批发货</th></tr></thead>
            <tbody>{lines.map((line) => <tr key={line.key}>
              <td><b>{line.name}</b>{line.detail && <small>{line.detail}</small>}</td>
              <td>{line.code}</td><td className={s.right}>{line.quantity}</td>
            </tr>)}</tbody>
          </table>
          {flat && <p className={s.note}>本单按产品组件列出；订单明细不可用时无法确认套装的套数。</p>}
          {loss > 0 && <p className={s.note}>本批另有 {qty(loss)} 个破损/补耗，未计入以上客户签收数量。</p>}
          {item.note && <p className={s.note}>备注：{item.note}</p>}
          <div className={s.sign}><span>发货方：________________</span><span>客户签收：________________</span><span>签收日期：________________</span></div>
        </article>
      </div>
    </section>
  </div>;
}
